"use client"
import { type AddressVerificationCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/addressVerificationCopy"
import { type ProductNoticesCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/productNoticesCopy"
import { OrderSummaryMinified } from "@/app/(site)/[locale]/checkout/CheckoutContent/components/OrderSummery/OrderSummaryMinified"
import NotificationBar from "@/app/(site)/[locale]/checkout/CheckoutContent/components/Sanity/NotificationBar"
import StepHeading from "@/app/(site)/[locale]/checkout/CheckoutContent/components/StepDetails/StepHeading"
import CustomerInformation from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation"
import Delivery from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/Delivery"
import StepSummary from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/StepSummary"
import { useStepValidation } from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/stepValidation"
import { type MarketLayout } from "@/app/(site)/[locale]/checkout/types"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { type CheckoutStep } from "@/app/(site)/contexts/ecommerce/types"
import logger from "@/lib/logger/logger-init"
import { innerStepClasses, outerStepClasses } from "@/lib/siteconfig/constants"

import { scrollToElement } from "@/lib/utils/scroll"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { type CheckoutPageSettingsContent } from "@/types"
import { useEffect, useRef } from "react"
import PaymentNew from "./Payment"

interface StepsProps {
    langCode: string
    params: { locale: string }
    marketLayout: MarketLayout
    notificationBar: CheckoutPageSettingsContent["notificationBar"] | null
    paymentPrivacyNotice: CheckoutPageSettingsContent["paymentPrivacyNotice"] | null
    addressVerificationCopy: AddressVerificationCopy
    productNoticesCopy: ProductNoticesCopy
}

export default function Steps({
    langCode,
    params,
    marketLayout,
    notificationBar,
    paymentPrivacyNotice,
    addressVerificationCopy,
    productNoticesCopy
}: StepsProps) {
    const { selection } = useSWRBasketSessionState()
    const { checkoutCurrentStep, setCheckoutCurrentStep, checkoutLoadedSteps } =
        useEcommerceStore()
    const { getValidStep, isLoadingValidation } = useStepValidation()
    const step1Ref = useRef<HTMLDivElement | null>(null)
    const step2Ref = useRef<HTMLDivElement | null>(null)
    const step1ContainerInputFieldsRef = useRef<HTMLDivElement | null>(null)
    const step1InputFieldsRef = useRef<HTMLDivElement | null>(null)

    // Validate and redirect to correct step based on data completeness
    useEffect(() => {
        if (checkoutCurrentStep === 1) {
            return // no need to run validation for step 1
        }
        if (!isLoadingValidation && selection) {
            const validStep = getValidStep(checkoutCurrentStep)
            // @todo: maybe add a radix dialog to communicate with the user?
            if (validStep !== checkoutCurrentStep) {
                logger.warn(
                    `Invalid step: checkoutCurrentStep: ${checkoutCurrentStep}, validStep: ${validStep}`
                )
                setCheckoutCurrentStep(validStep)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingValidation, checkoutCurrentStep])

    // This is is to make sure the height is adjusted when switching between countries with and without states
    // TODO: Move sections into Accordion Context
    const useAccordionHeight = (
        containerRef: React.RefObject<HTMLDivElement | null>,
        contentRef: React.RefObject<HTMLDivElement | null>,
        isOpen: boolean
    ) => {
        useEffect(() => {
            const container = containerRef.current
            const content = contentRef.current
            if (!container || !content) return

            const mq = window.matchMedia("(min-width: 768px)") // md breakpoint
            const setHeight = () => {
                container.style.height = isOpen ? `${content.offsetHeight}px` : "0px"
            }

            const raf = requestAnimationFrame(setHeight)

            const ro =
                typeof ResizeObserver !== "undefined"
                    ? new ResizeObserver(() => setHeight())
                    : null
            ro?.observe(content)
            mq.addEventListener?.("change", setHeight)
            window.addEventListener("resize", setHeight)

            return () => {
                cancelAnimationFrame(raf)
                ro?.disconnect()
                mq.removeEventListener?.("change", setHeight)
                window.removeEventListener("resize", setHeight)
            }
        }, [containerRef, contentRef, isOpen])
    }

    useAccordionHeight(
        step1ContainerInputFieldsRef,
        step1InputFieldsRef,
        checkoutCurrentStep === 1
    )

    const accordionAnimationClasses = (step: CheckoutStep) =>
        `transition-all duration-300 ease-in-out ${checkoutCurrentStep === step ? "h-full opacity-100" : "max-h-0 a11y-overflow-clip opacity-0"}`

    return (
        <div className="a11y-overflow-clip relative h-full !border-t-0 sm:border sm:border-col-border md:border-b-0">
            <div className="h-full overflow-y-auto scrollbar-hide">
                {/* Step 1 - Customer Information */}
                <div className={clsx(outerStepClasses, "pt-32")}>
                    <div className={clsx(innerStepClasses)} ref={step1Ref}>
                        {notificationBar && (
                            <NotificationBar
                                params={params}
                                notificationBar={notificationBar}
                            />
                        )}

                        <StepHeading
                            langCode={langCode}
                            title={t(langCode, "checkout.shipping_address")}
                            toStep={1}
                            className={`${checkoutCurrentStep === 1 ? "hidden" : "pb-16 text-col-text-tertiary hover:text-col-text-primary"}`}
                            onEditClick={() => {
                                if (step1Ref.current) {
                                    setTimeout(() => {
                                        scrollToElement(step1Ref.current, {
                                            smooth: true,
                                            yOffset: 0
                                        })
                                    }, 100)
                                }
                            }}
                        />

                        {checkoutCurrentStep !== 1 && checkoutLoadedSteps.includes(1) && (
                            <StepSummary
                                step={1}
                                langCode={langCode}
                                className="pb-32"
                                marketLayout={marketLayout}
                            />
                        )}

                        <div
                            ref={step1ContainerInputFieldsRef}
                            className={clsx("a11y-overflow-clip min-w-0", "-mx-8 px-8")}
                            style={{ height: 0 }}
                        >
                            <div ref={step1InputFieldsRef}>
                                {checkoutLoadedSteps.includes(1) && (
                                    <CustomerInformation
                                        setStep={setCheckoutCurrentStep}
                                        langCode={langCode}
                                        marketLayout={marketLayout}
                                        addressVerificationCopy={addressVerificationCopy}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 2 - Delivery */}
                <div ref={step2Ref} className={outerStepClasses}>
                    <div className={innerStepClasses}>
                        <div
                            className={clsx(
                                "flex flex-col",
                                checkoutCurrentStep !== 2 &&
                                    checkoutLoadedSteps.includes(2) &&
                                    "gap-24"
                            )}
                        >
                            <StepHeading
                                langCode={langCode}
                                title={t(langCode, "checkout.shipping.shipping_method")}
                                toStep={2}
                                className={clsx(
                                    checkoutCurrentStep === 2 && "pb-24 pt-32",
                                    checkoutCurrentStep === 3 &&
                                        "hover:[&_h3]:text-col-text-primary",
                                    checkoutCurrentStep !== 2 && "pt-32"
                                )}
                                titleClassName={clsx(
                                    checkoutCurrentStep !== 2 && "text-col-text-tertiary"
                                )}
                                onEditClick={() => {
                                    if (step2Ref.current) {
                                        setTimeout(() => {
                                            scrollToElement(step2Ref.current, {
                                                smooth: true,
                                                yOffset: 0
                                            })
                                        }, 100)
                                    }
                                }}
                            />
                            {checkoutCurrentStep !== 2 &&
                                checkoutLoadedSteps.includes(2) && (
                                    <StepSummary step={2} langCode={langCode} />
                                )}
                        </div>

                        <div className={clsx(accordionAnimationClasses(2))}>
                            {checkoutLoadedSteps.includes(2) && (
                                <Delivery
                                    setStep={setCheckoutCurrentStep}
                                    langCode={langCode}
                                    paymentPrivacyNotice={
                                        paymentPrivacyNotice ?? undefined
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Step 3 - Payment */}
                <div className={clsx(outerStepClasses, "mb-82 md:mb-0")}>
                    <div className={clsx(innerStepClasses)}>
                        <div>
                            <StepHeading
                                langCode={langCode}
                                title={t(langCode, "checkout.payment")}
                                toStep={3}
                                className={`${checkoutCurrentStep === 3 ? "pb-24 pt-32" : "py-24 text-col-text-tertiary lg:py-32"}`}
                            />
                        </div>
                        {checkoutCurrentStep === 3 && checkoutLoadedSteps.includes(3) && (
                            <>
                                <div className="pb-24 md:hidden md:pb-0">
                                    <OrderSummaryMinified
                                        marketLayout={marketLayout}
                                        langCode={langCode}
                                        showShipping={true}
                                        productNoticesCopy={productNoticesCopy}
                                    />
                                </div>

                                <div className={clsx(accordionAnimationClasses(3))}>
                                    <PaymentNew langCode={langCode} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
